import { Input, VisualInput } from '@/components/inputs';
import { stashDataSource } from '@/features/dataSources/dataSourcesSlice';
import { useWorkspace } from '@/features/workspaces/hooks';
import { StepPageActions } from '@/global/components';
import { DATA_SOURCE_ICONS } from '@/lib/Icons/DataSourceIcons/constants';
import { DataSource, DataSourceCodeConfig, DataSourceIDEnum, DataSourceType } from '@/models/dataSource';
import { RootState, useAppDispatch } from '@/reducers';
import { useSaveDataSourceMutation } from '@/services/dataSources';
import { Form, Formik, FormikProps } from 'formik';
import { FC, SVGProps, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import * as Yup from 'yup';

type ShopifySourceDetailsStepProps = {
  dataSourceType: DataSourceType;
  onNextStep?: VoidFunction;
  onCancel?: VoidFunction;
};

type ShopifySourceDetailsForm = Partial<DataSource<DataSourceCodeConfig>>;

export const ShopifySourceDetailsStep: FC<ShopifySourceDetailsStepProps> = ({ dataSourceType, onNextStep, onCancel }) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const dataSourceTypeId = dataSourceType.id as DataSourceIDEnum;
  const RawSourceDetailsIcon = DATA_SOURCE_ICONS[dataSourceTypeId];
  const SourceDetailsIcon = (props: SVGProps<SVGSVGElement>) => <RawSourceDetailsIcon className={props.className ?? 'size-[65px]'} />;
  const dataSource = useSelector((state: RootState) => state.dataSources.edit?.dataSource) as ShopifySourceDetailsForm | undefined;

  const dataSourceInfoFormRef = useRef<FormikProps<ShopifySourceDetailsForm>>(null);

  const workspace = useWorkspace();
  const [saveDataSource, { isLoading: isSaveLoading }] = useSaveDataSourceMutation();

  const validationSchema = Yup.object({
    name: Yup.string()
      .min(3, t('data_source_edit:validations.too_short'))
      .max(50, t('data_source_edit:validations.too_long'))
      .required(t('data_source_edit:validations.required')),
  });

  const handleNextStep = async () => {
    await dataSourceInfoFormRef.current?.submitForm();
    try {
      if (dataSourceInfoFormRef.current?.isValid && dataSourceInfoFormRef.current?.values.name) {
        const response = await saveDataSource({
          workspaceId: workspace.id,
          dataSource: {
            type: DataSourceIDEnum.Shopify,
            name: dataSourceInfoFormRef.current.values.name,
            configuration: { url: 'https://your-shopify-store.myshopify.com' },
          },
        }).unwrap();
        dispatch(stashDataSource(response.dataSource));
        onNextStep?.();
      }
    } catch {
      // Stay on Source Details so the user can retry after a failed create.
    }
  };

  return (
    <Formik
      innerRef={dataSourceInfoFormRef}
      initialValues={{ ...dataSource }}
      onSubmit={() => undefined}
      validateOnBlur={false}
      validateOnChange={false}
      validationSchema={validationSchema}
    >
      {({ values, errors, handleChange, setFieldError }) => {
        return (
          <>
            <div className='flex grow gap-8'>
              <div className='flex grow flex-col gap-3'>
                <Form>
                  <VisualInput
                    name='type'
                    id='type'
                    label={t('data_source_edit:code.source_details.source_type')}
                    value={t(`data_sources:catalog.${dataSourceTypeId}.title`)}
                    icon={SourceDetailsIcon}
                  />
                  <Input
                    name='name'
                    id='name'
                    type='text'
                    label={t('data_source_edit:code.source_details.source_name')}
                    value={values.name}
                    placeholder={t('data_source_edit:shopify.source_details.source_name_placeholder')}
                    onChange={(e) => {
                      setFieldError('name', undefined);
                      handleChange('name')(e.target.value);
                    }}
                    error={errors.name}
                  />
                </Form>
              </div>
            </div>

            <StepPageActions
              primaryButton={{
                text: 'COMMON.confirm',
                onClick: handleNextStep,
                isLoading: isSaveLoading,
              }}
              secondaryButton={{
                text: 'COMMON.cancel',
                onClick: onCancel,
              }}
            />
          </>
        );
      }}
    </Formik>
  );
};
